import React, { useState, useEffect, useCallback } from 'react';
import {
  Timer, CheckCircle, XCircle, AlertTriangle, RefreshCw, Clock,
  PauseCircle, MinusCircle, ChevronDown, ChevronUp, ArrowUpDown
} from 'lucide-react';
import { fetchWithCsrf } from '../lib/fetchWithCsrf';
import OpsMonitor from './OpsMonitor';
import DisputedRecords from './DisputedRecords';
import QualityStops from './QualityStops';

// ─── Health config ───────────────────────────────────────────────────────────

const HEALTH_CFG = {
  healthy:  { label: 'Healthy',  classes: 'bg-green-100 text-green-800',   Icon: CheckCircle,    blurb: 'Last run succeeded' },
  failing:  { label: 'Failing',  classes: 'bg-red-100 text-red-800',       Icon: XCircle,        blurb: 'Most recent run errored' },
  stale:    { label: 'Stale',    classes: 'bg-orange-100 text-orange-800', Icon: AlertTriangle,  blurb: 'Has not run when it should have' },
  degraded: { label: 'Degraded', classes: 'bg-yellow-100 text-yellow-800', Icon: AlertTriangle,  blurb: 'Recovered, but failed earlier in the window' },
  idle:     { label: 'Idle',     classes: 'bg-gray-100 text-gray-600',     Icon: MinusCircle,    blurb: 'No runs in the selected window' },
  paused:   { label: 'Paused',   classes: 'bg-gray-100 text-gray-500',     Icon: PauseCircle,    blurb: 'Job is inactive' },
};

const SUMMARY_ORDER = ['failing', 'stale', 'degraded', 'idle', 'paused', 'healthy'];
const WINDOWS = [
  { key: '1h', label: '1h' },
  { key: '6h', label: '6h' },
  { key: '24h', label: '24h' },
  { key: '7d', label: '7d' },
];

function HealthBadge({ health }) {
  const cfg = HEALTH_CFG[health] || HEALTH_CFG.idle;
  const { label, classes, Icon } = cfg;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${classes}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

function relativeTime(iso) {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  const diffSec = Math.round((Date.now() - then) / 1000);
  if (diffSec < 0) return 'in the future';
  if (diffSec < 60) return `${diffSec}s ago`;
  const min = Math.round(diffSec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 48) return `${hr}h ago`;
  const days = Math.round(hr / 24);
  return `${days}d ago`;
}

function cadenceHint(minutes) {
  if (minutes == null) return null;
  if (minutes < 60) return `~every ${minutes}m`;
  if (minutes < 1440) return `~every ${Math.round(minutes / 60)}h`;
  return `~daily`;
}

// ─── Run-history drill-down ──────────────────────────────────────────────────

function RunHistory({ jobname }) {
  const [runs, setRuns] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithCsrf(`/api/admin/cron/runs?job=${encodeURIComponent(jobname)}&limit=25`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || `HTTP ${res.status}`);
        }
        const data = await res.json();
        if (!cancelled) setRuns(data.runs || []);
      } catch (e) {
        if (!cancelled) setErr(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [jobname]);

  if (err) return <div className="text-sm text-red-600 px-4 py-3">Failed to load runs: {err}</div>;
  if (runs === null) return <div className="text-sm text-gray-500 px-4 py-3">Loading run history…</div>;
  if (runs.length === 0) return <div className="text-sm text-gray-500 px-4 py-3">No runs recorded.</div>;

  return (
    <div className="px-4 py-3 space-y-1.5">
      {runs.map((r, i) => (
        <div key={i} className="flex items-start gap-3 text-xs">
          <span className={`mt-0.5 inline-flex w-16 flex-shrink-0 items-center gap-1 font-medium ${
            r.status === 'succeeded' ? 'text-green-700' : 'text-red-700'
          }`}>
            {r.status === 'succeeded' ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
            {r.status}
          </span>
          <span className="w-32 flex-shrink-0 text-gray-500">{new Date(r.start_time).toLocaleString()}</span>
          <span className="text-gray-700 break-all whitespace-pre-wrap">{r.return_message}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Cron Health tab (original page body — unchanged) ─────────────────────────

function CronHealthTab() {
  const [jobs, setJobs] = useState([]);
  const [summary, setSummary] = useState({});
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [windowKey, setWindowKey] = useState('24h');
  const [healthFilter, setHealthFilter] = useState('all');
  const [expanded, setExpanded] = useState(null);
  // Default sort matches the server's natural order: failing first, then
  // stale/degraded/idle/paused/healthy. Clicking a column header replaces
  // this with that column's sort (toggling asc/desc on repeat clicks).
  const [sortConfig, setSortConfig] = useState({ key: 'health', dir: 'asc' });

  // Numeric ordering for health (failing first, healthy last). Used by the
  // health-column sort and as the default tiebreaker.
  const HEALTH_RANK = { failing: 0, stale: 1, degraded: 2, idle: 3, paused: 4, healthy: 5 };

  function toggleSort(key) {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      // Sensible default direction per column: ts columns desc (newest first),
      // numeric desc (biggest fails first), everything else asc.
      const numericOrTimeCols = ['last_run_at', 'last_success_at', 'fail_count'];
      return { key, dir: numericOrTimeCols.includes(key) ? 'desc' : 'asc' };
    });
  }

  function applySort(rows) {
    const { key, dir } = sortConfig;
    const mul = dir === 'asc' ? 1 : -1;
    const sorted = [...rows].sort((a, b) => {
      let av, bv;
      if (key === 'health') {
        av = HEALTH_RANK[a.health] ?? 99;
        bv = HEALTH_RANK[b.health] ?? 99;
      } else if (key === 'jobname') {
        av = (a.jobname || '').toLowerCase();
        bv = (b.jobname || '').toLowerCase();
      } else if (key === 'schedule') {
        // Sort schedule by estimated interval (most frequent first when asc).
        av = a.estimated_interval_minutes ?? Number.MAX_SAFE_INTEGER;
        bv = b.estimated_interval_minutes ?? Number.MAX_SAFE_INTEGER;
      } else if (key === 'last_run_at' || key === 'last_success_at') {
        av = a[key] ? new Date(a[key]).getTime() : 0;
        bv = b[key] ? new Date(b[key]).getTime() : 0;
      } else if (key === 'fail_count') {
        av = a.fail_count || 0;
        bv = b.fail_count || 0;
      } else {
        av = a[key];
        bv = b[key];
      }
      if (av < bv) return -1 * mul;
      if (av > bv) return  1 * mul;
      // Tiebreaker by jobname so order is stable.
      return (a.jobname || '').localeCompare(b.jobname || '');
    });
    return sorted;
  }

  function SortHeader({ label, sortKey, align = 'left' }) {
    const isActive = sortConfig.key === sortKey;
    const arrow = isActive
      ? (sortConfig.dir === 'asc'
          ? <ChevronUp className="w-3 h-3" />
          : <ChevronDown className="w-3 h-3" />)
      : <ArrowUpDown className="w-3 h-3 text-gray-300" />;
    return (
      <th className={`text-${align} font-medium px-4 py-3 select-none`}>
        <button
          onClick={() => toggleSort(sortKey)}
          className={`inline-flex items-center gap-1 hover:text-gray-900 ${isActive ? 'text-gray-900' : ''}`}
        >
          {label}
          {arrow}
        </button>
      </th>
    );
  }

  const fetchData = useCallback(async (isRefresh = false) => {
    try {
      isRefresh ? setRefreshing(true) : setLoading(true);
      setError(null);
      const res = await fetchWithCsrf(`/api/admin/cron/health?window=${windowKey}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setJobs(data.jobs || []);
      setSummary(data.summary || {});
      setGeneratedAt(data.generated_at || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [windowKey]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const problemCount = (summary.failing || 0) + (summary.stale || 0);
  const filtered = applySort(
    healthFilter === 'all' ? jobs : jobs.filter(j => j.health === healthFilter)
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <Timer className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Cron Health</h1>
            <p className="text-sm text-gray-500">
              Scheduled jobs and their run status
              {generatedAt && <> · updated {relativeTime(generatedAt)}</>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
            {WINDOWS.map(w => (
              <button
                key={w.key}
                onClick={() => setWindowKey(w.key)}
                className={`px-3 py-1.5 text-sm font-medium ${
                  windowKey === w.key ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => fetchData(true)}
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Top-of-page alert when something is wrong */}
      {!loading && problemCount > 0 && (
        <div className="mb-4 flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>
            <strong>{problemCount}</strong> job{problemCount > 1 ? 's need' : ' needs'} attention
            {summary.failing ? ` · ${summary.failing} failing` : ''}
            {summary.stale ? ` · ${summary.stale} stale` : ''}.
          </span>
        </div>
      )}

      {/* Summary chips (click to filter) */}
      <div className="flex flex-wrap gap-2 mb-5">
        <button
          onClick={() => setHealthFilter('all')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${
            healthFilter === 'all' ? 'border-indigo-300 bg-indigo-50 text-indigo-700' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
          }`}
        >
          All ({jobs.length})
        </button>
        {SUMMARY_ORDER.filter(h => summary[h]).map(h => {
          const cfg = HEALTH_CFG[h];
          return (
            <button
              key={h}
              onClick={() => setHealthFilter(h)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border ${
                healthFilter === h ? 'border-indigo-300 bg-indigo-50 text-indigo-700' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <cfg.Icon className="w-3.5 h-3.5" />
              {cfg.label} ({summary[h]})
            </button>
          );
        })}
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
              <tr>
                <SortHeader label="Status"       sortKey="health" />
                <SortHeader label="Job"          sortKey="jobname" />
                <SortHeader label="Schedule"     sortKey="schedule" />
                <SortHeader label="Last run"     sortKey="last_run_at" />
                <SortHeader label="Last success" sortKey="last_success_at" />
                <SortHeader label="Fails"        sortKey="fail_count" align="right" />
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(job => {
                const isOpen = expanded === job.jobname;
                const hint = cadenceHint(job.estimated_interval_minutes);
                return (
                  <React.Fragment key={job.jobname}>
                    <tr
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setExpanded(isOpen ? null : job.jobname)}
                    >
                      <td className="px-4 py-3"><HealthBadge health={job.health} /></td>
                      <td className="px-4 py-3 font-medium text-gray-900">{job.jobname}</td>
                      <td className="px-4 py-3 text-gray-500">
                        <code className="text-xs">{job.schedule}</code>
                        {hint && <span className="ml-2 text-gray-400">{hint}</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        <span className="inline-flex items-center gap-1">
                          <Clock className="w-3 h-3 text-gray-400" />
                          {relativeTime(job.last_run_at)}
                        </span>
                      </td>
                      <td className={`px-4 py-3 ${job.last_success_at ? 'text-gray-600' : 'text-red-600 font-medium'}`}>
                        {relativeTime(job.last_success_at)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={job.fail_count > 0 ? 'text-red-600 font-semibold' : 'text-gray-400'}>
                          {job.fail_count}
                        </span>
                        {job.transient_fail_count > 0 && (
                          <div
                            className="text-[11px] text-gray-400 whitespace-nowrap"
                            title="pg_cron launcher blips (connection failed / job startup timeout) — the scheduled tick never started, so the job itself did not error. Usually a brief database-wide hiccup."
                          >
                            +{job.transient_fail_count} transient
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-400">
                        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-gray-50/60">
                        <td colSpan={7} className="px-0 py-0">
                          {job.last_error && (
                            <div className="px-4 pt-3">
                              <div className="text-xs font-semibold text-red-700 mb-1">Last error</div>
                              <pre className="text-xs text-red-800 bg-red-50 border border-red-200 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap">{job.last_error}</pre>
                            </div>
                          )}
                          <div className="text-xs font-semibold text-gray-500 px-4 pt-3">Recent runs</div>
                          <RunHistory jobname={job.jobname} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">No jobs match this filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Page shell: Cron Health + Ops Monitor + Disputed Records tabs ────────────

const TABS = [
  { key: 'cron', label: 'Cron Health' },
  { key: 'ops', label: 'Ops Monitor' },
  { key: 'disputes', label: 'Disputed Records' },
  { key: 'quality', label: 'Quality Stops' },
];

export default function CronHealthDashboard() {
  const [tab, setTab] = useState('cron');
  return (
    <div>
      <div className="px-6 pt-6 max-w-7xl mx-auto">
        <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-1.5 text-sm font-medium ${
                tab === t.key ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      {tab === 'cron' && <CronHealthTab />}
      {tab === 'ops' && <OpsMonitor />}
      {tab === 'disputes' && <DisputedRecords />}
      {tab === 'quality' && <QualityStops />}
    </div>
  );
}
